"use client";

import classNames from "classnames";
import React, { useState } from "react";

import { ArticleCard } from "@/components/blog/article-card";
import { ArticleHeaderCard } from "@/components/blog/article-header-card";
import { Divider } from "@/components/helper/divider";
import { LanguageProps } from "@/i18n";
import { GetType } from "@/types/types";

type Blog = GetType<"api::blog.blog">;
type Microblog = GetType<"api::microblog.microblog">;
type Category = GetType<"api::categorie.categorie">;

interface Props extends LanguageProps {
  content: Blog;
  articles: Microblog[];
  blogCategoryList: Category[];
}

/**
 * This JSX component build the main render function for the blog overview page.
 * @param lang Language the page is currently viewed in.
 * @param content The cms object that holds the website content.
 * @param articles The list of available articles.
 * @param blogCategoryList A list of blog categories.
 * @returns JSX UI for blog page.
 */
export function Blog({ lang, content, articles, blogCategoryList }: Props) {
  const [filter, setFilter] = useState(0);

  // Creates a list of Categories where we have at least one article in.
  const categories = [
    { id: 0, categorie_name: lang === "de" ? "Alle" : "All" },
    ...blogCategoryList.filter((category) => {
      return articles.some((article) =>
        (article.categories?.data ?? []).some(
          (articleCategory) => articleCategory.id === category.id,
        ),
      );
    }),
  ];

  // Filters and sorts the articles by selected filter and then by date.
  const filteredArticles = articles
    .filter(
      (article) =>
        filter === 0 || article.categories?.data?.some((y) => y.id === filter),
    )
    .sort((a, b) => (a.publish_date > b.publish_date ? -1 : 1));

  const newestArticle = filteredArticles[0];

  return (
    <div className="blog-bg gradient-container w-100">
      <div className="blog-grid-new">
        {/* Header Section */}
        <div className="header-grid-element-one">
          <h1 className="mid-title-text text-start pb-0 m-0 text-nowrap me-4">
            {content.headline}
          </h1>
        </div>

        <div className="header-card-grid-element-two">
          <div className="hide-mobile">
            <ArticleHeaderCard article={newestArticle} lang={lang} />
          </div>

          <div className="hide-tablet hide-desktop">
            <ArticleCard lang={lang} article={newestArticle} />
          </div>
        </div>

        <div className="blog-grid-full-w">
          <Divider size="full" />
        </div>
        {/* Filter Options */}
        <div className="blog-grid-full-w">
          {blogCategoryList.length > 0 && (
            <div className="d-flex align-items-center justify-content-center  pt-3">
              <div className="blog-filter-section">
                {categories.map((x, index) => (
                  <React.Fragment key={x.id}>
                    {index !== 0 && <span> | </span>}
                    <span
                      onClick={() => setFilter(x.id)}
                      className={classNames("clickable", {
                        active: filter === x.id,
                      })}
                    >
                      {x.categorie_name?.toUpperCase()}
                    </span>
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Articles */}
        {filteredArticles.slice(1).map((article) => (
          <div key={article.id} className="blog-grid-item">
            <ArticleCard lang={lang} article={article} />
          </div>
        ))}
      </div>
    </div>
  );
}
